import {
  ADMIN_CHAT,
  ADMIN_LOCATIONS,
  ADMIN_LOCATIONS_CONNECT_BRANDS,
  ADMIN_SUPPORT_TICKETS,
  ADMIN_SYSTEM_PROBLEMS,
  ADMIN_VERIFICATIONS,
  ArrowRed,
  Disclaimer,
  Div,
  File,
  H5,
  Icon as Ic,
  InputArrowIcon,
  Logout,
  NewPillIcon,
  P,
  PARTNER_LOCATIONS_CHOOSE_LOCATION_TO_ADD_BRAND,
  PARTNER_V_DRIVE,
  RouteObjectType,
  User,
  UserPermission,
  UserRole,
  getPartnerLocations,
  hasTwoWeeksPassed,
  isAdmin,
  isDeliveries,
  isPartner,
  lightTheme,
  renderRole,
  useAuth,
  useUserConversationUnreadCount,
  useChatUnreadCount,
  usePermissions,
  useSupportTicketOpenCount,
  useSystemProblemsOpenCount,
  useSidebarState,
  useVerificationsCount,
  VDriveLogoWhiteCircle,
} from '@vgrubs/components';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
// Direct import: the prototype chat store is deliberately not on the barrel.
import { useDeliveriesChatUnreadCount } from '../../../hooks/useDeliveriesChatUnreadCount';
import { AdminLogoutModal } from './AdminLogoutModal';
import { SIDEBAR_WIDTH, SIDEBAR_WIDTH_COLLAPSED } from './layoutMetrics';

const AdminSidebarStyled = styled.div<{ $sidebarIsOpen: boolean }>`
  width: ${({ $sidebarIsOpen }) =>
    $sidebarIsOpen ? SIDEBAR_WIDTH : SIDEBAR_WIDTH_COLLAPSED};
  flex: none;
  /* height: 100vh; */
  background-color: ${({ theme }) => theme.primary};
  padding: 24px 0;
  border-radius: 0 20px 20px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  overflow-y: auto;
  z-index: 1;

  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  transition: ${({ theme }) => theme.transitions.ease200ms};

  *:focus-visible {
    outline-color: ${({ theme }) => theme.white};
  }

  span {
    font-weight: 700;
    font-size: 14px;
    line-height: 20px;
    color: ${({ theme }) => theme.white};
  }

  @media (max-width: 1450px) {
    width: ${({ $sidebarIsOpen }) =>
      $sidebarIsOpen ? SIDEBAR_WIDTH : SIDEBAR_WIDTH_COLLAPSED};

    span {
      font-size: 14px;
    }
  }

  @media (max-width: 800px) {
    left: ${({ $sidebarIsOpen }) => ($sidebarIsOpen ? '0px' : '-100%')};

    max-width: 100%;
    width: 100%;
    border-radius: 0;
    /* padding:  32px; */
    z-index: 2147483648;

    span {
      font-size: 16px;
    }
  }
`;

const AdminNavigationStyled = styled.nav`
  width: 100%;
`;

const NavLinkCircle = styled.nav<{ selected?: boolean; defaultColor?: string }>`
  position: relative;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ theme, selected }) =>
    selected ? theme.white : `${theme.white}${theme.transparency.t10}`};
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  margin-right: 12px;
  transition: all 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
    path {
      transition: all 0.2s ease;

      fill: ${({ theme, selected, defaultColor }) =>
        defaultColor ? defaultColor : selected ? theme.primary : theme.white};
    }
  }

  @media (max-width: 800px) {
    width: 44px;
    height: 44px;
    margin-right: 16px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

export const MobileSidebarOverlay = styled.div<{ $sidebarIsOpen: boolean }>`
  background-color: ${({ theme }) => theme.primary};
  /* background-color: ${({ theme }) => theme.darkGray}4D; */
  opacity: ${({ $sidebarIsOpen }) => ($sidebarIsOpen ? '1' : '0')};
  visibility: ${({ $sidebarIsOpen }) =>
    $sidebarIsOpen ? 'visible' : 'hidden'};
  transition: ${({ theme }) => theme.transitions.ease300ms};
  transition-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  z-index: 8;
  display: none;

  @media (max-width: 800px) {
    display: block;
  }
`;

export const AdminSidebar = ({
  routes,
  sidebarTop,
}: {
  routes: RouteObjectType[];
  /** Rendered between the brand header and the navigation (e.g. an org switcher). */
  sidebarTop?: React.ReactNode;
}) => {
  const pathname = useLocation().pathname;
  // Through ThemeProvider so a navy-primary app recolours the count bubbles.
  const theme = useTheme();

  const selected = (path: string) => {
    if (pathname.startsWith('/brand') && path === ADMIN_LOCATIONS) return true;
    if (pathname.startsWith('/menu') && path === ADMIN_LOCATIONS) return true;

    return pathname.startsWith(path);
  };

  const navigate = useNavigate();
  const [logoutModal, setLogoutModal] = useState(false);

  const { role, phoneNumber } = useAuth((state) => state?.user as User);

  const {
    sidebarIsOpen: sidebarIsOpenState,
    setSidebarIsOpen: setSidebarIsOpenState,
  } = useSidebarState((state) => state);

  const sidebarIsOpen = sidebarIsOpenState;
  const setSidebarIsOpen = (value: boolean) => setSidebarIsOpenState(value);

  const { count } = useVerificationsCount({ enabled: role === UserRole.admin });

  const { can } = usePermissions();

  /*
   * Role, not `conversationRead`: the badge counts groups and direct messages
   * too, and membership in those needs no permission.
   */
  const { count: adminUnread } = useChatUnreadCount({
    enabled:
      isAdmin &&
      (role === UserRole.admin ||
        role === UserRole.support ||
        role === UserRole.marketing),
  });
  const { total: memberUnread } = useUserConversationUnreadCount({
    enabled:
      isPartner &&
      (role === UserRole.partner ||
        role === UserRole.manager ||
        role === UserRole.staff),
  });

  /*
   * Tickets, unlike the chat badge above, ARE gated on the permission: the
   * page they count for is, and a number nobody can click through to is worse
   * than no number.
   */
  const { open: openTickets } = useSupportTicketOpenCount({
    enabled:
      isAdmin &&
      (role === UserRole.admin ||
        role === UserRole.support ||
        role === UserRole.marketing) &&
      can(UserPermission.conversationRead),
  });

  const { open: openProblems } = useSystemProblemsOpenCount({
    enabled:
      isAdmin && (role === UserRole.admin || role === UserRole.support),
  });

  // The deliveries app's badge: unread driver messages in the selected
  // organization, from the API and kept fresh by the /chat socket.
  const deliveriesUnread = useDeliveriesChatUnreadCount({
    enabled: isDeliveries,
  });

  const [windowWidth, setWindowWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 800
  );

  const { locations, saveLocations } = useAuth((state) => state);

  const getLocations = async () => {
    // Staff only reach the partner app for chat and don't own locations, so
    // this call isn't theirs to make. Guarded here because there's no catch -
    // an unhandled rejection is the whole sidebar's problem.
    if (!isPartner || role === UserRole.staff) return;
    const { data } = await getPartnerLocations({
      page: 1,
      limit: 2,
      verified: true,
    });
    saveLocations(data.data);
  };

  useEffect(() => {
    getLocations();
  }, []);

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const XIcon = styled.div<{ $sidebarIsOpen: boolean }>`
    position: absolute;
    right: ${({ $sidebarIsOpen }) => ($sidebarIsOpen ? '16px' : '18px')};
    top: ${({ $sidebarIsOpen }) => ($sidebarIsOpen ? '-12px' : '-4px')};
    rotate: ${({ $sidebarIsOpen }) => ($sidebarIsOpen ? '0deg' : '180deg')};
  `;

  const isExpandedDesktop =
    windowWidth < 800 || (windowWidth > 800 && sidebarIsOpen);

  const CountBubble = ({ value }: { value: number }) => {
    if (value === 0) return null;

    return (
      <Div
        $background={lightTheme.white}
        $borderRadius="50%"
        $width="24px"
        $height="24px"
        $widthSM="28px"
        $heightSM="28px"
        $flex
        $center
        $flexNone
      >
        <Disclaimer $m="0" $color={theme.primary} $fsSM="14px">
          {value > 99 ? '99+' : value}
        </Disclaimer>
      </Div>
    );
  };

  const VerificationsCount = () => (
    <CountBubble
      value={count.locations + count.agents + count.vDrive + count.brands}
    />
  );

  /**
   * Unread chat. Kept fresh by the socket rather than a poll, which is why
   * neither hook here sets a `refetchInterval`. The two apps read different
   * response shapes from the same endpoint; on the merchant side it is the
   * support thread and the groups together, since one nav item leads to both.
   */
  const ChatUnreadCount = () => (
    <CountBubble
      value={
        isDeliveries
          ? deliveriesUnread
          : isAdmin
          ? adminUnread.messages
          : memberUnread
      }
    />
  );

  const SupportTicketsCount = () => <CountBubble value={openTickets} />;

  const SystemProblemsCount = () => <CountBubble value={openProblems} />;

  /**
   * Which nav items wear a number, in one place.
   *
   * The two render sites below - the collapsed rail's floating badge and the
   * expanded row's right-hand slot - each used to name the paths themselves,
   * which is how a third one gets added to one of them and forgotten in the
   * other.
   */
  const badgeFor = (path: string) => {
    if (path === ADMIN_CHAT) return ChatUnreadCount;
    if (path === ADMIN_VERIFICATIONS) return VerificationsCount;
    if (path === ADMIN_SUPPORT_TICKETS) return SupportTicketsCount;
    if (path === ADMIN_SYSTEM_PROBLEMS) return SystemProblemsCount;
    return null;
  };

  return (
    <>
      <AdminSidebarStyled $sidebarIsOpen={sidebarIsOpen}>
        <Div
          $width="100%"
          $flex
          $column
          $relative
          // $alignCenter={!sidebarIsOpen}
        >
          <Div
            $flex
            $between
            style={{
              position: 'relative',
              top: window.innerWidth > 800 ? (!sidebarIsOpen ? 44 : 12) : 0,
              transition: '.2s all ease',
              padding:
                window.innerWidth > 800 && !sidebarIsOpen ? '0 12px' : '0 24px',
            }}
            $height="90px"
            $heightSM="90px"
          >
            <Div
              $flex
              $alignCenter
              $gap={'16px'}
              $opacity={isExpandedDesktop ? '1' : '0'}
              style={{
                transition: `.2s all  ${isExpandedDesktop ? '.2s' : ''}`,
                position: 'absolute',
              }}
            >
              <Ic $size={'80'} style={{ transition: '.2s all ease' }}>
                {isDeliveries ? <VDriveLogoWhiteCircle /> : <LogoWhiteCircle />}
              </Ic>
              <Div $column>
                <H5
                  $m="0"
                  $color={lightTheme.white}
                  style={{ whiteSpace: 'nowrap' }}
                  $fsSM="18px"
                >
                  {renderRole(role, true)}
                </H5>
                <Div $column $gap="0" $mt={5}>
                  <Disclaimer $m="0" $color={lightTheme.white} $fsSM="14px">
                    {phoneNumber}
                  </Disclaimer>
                </Div>
              </Div>
            </Div>
            <Div
              $flex
              $alignCenter
              $gap={'8px'}
              $column
              $opacity={!isExpandedDesktop ? '1' : '0'}
              style={{
                transition: `.2s all  ${!isExpandedDesktop ? '.2s' : ''}`,
                position: 'absolute',
              }}
            >
              <Ic $size={'60'} style={{ transition: '.2s all ease' }}>
                {isDeliveries ? <VDriveLogoWhiteCircle /> : <LogoWhiteCircle />}
              </Ic>
              <P
                $m="0"
                $bold
                $color={lightTheme.white}
                style={{
                  whiteSpace: 'nowrap',
                }}
                $fsSM="18px"
                $center
              >
                {renderRole(role, true)}
              </P>
            </Div>
          </Div>

          <XIcon $sidebarIsOpen={sidebarIsOpen}>
            <Div
              $flex
              $alignCenter
              $justifyEnd
              $width="40px"
              $height="40px"
              $heightSM="40px"
              onClick={() => setSidebarIsOpen(!sidebarIsOpen)}
              $pointer
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  opacity="0.1"
                  width="32"
                  height="32"
                  rx="8"
                  fill="white"
                />
                <path
                  d="M19.4103 20.59L14.8303 16L19.4103 11.41L18.0003 10L12.0003 16L18.0003 22L19.4103 20.59Z"
                  fill="white"
                />
              </svg>
            </Div>
          </XIcon>

          {sidebarTop && (
            <Div
              style={{
                position: 'relative',
                top: window.innerWidth > 800 ? 54 : 16,
                transition: '.2s all ease',
                padding: '0 24px',
                // No icon-only form, so it folds away with the collapsed
                // rail. Overflow stays visible while expanded - the org
                // switcher opens a dropdown that must not be clipped.
                opacity: window.innerWidth > 800 && !sidebarIsOpen ? 0 : 1,
                height: window.innerWidth > 800 && !sidebarIsOpen ? 0 : 'auto',
                overflow:
                  window.innerWidth > 800 && !sidebarIsOpen
                    ? 'hidden'
                    : 'visible',
                pointerEvents:
                  window.innerWidth > 800 && !sidebarIsOpen ? 'none' : 'auto',
                marginBottom:
                  window.innerWidth > 800 && !sidebarIsOpen ? 0 : 16,
              }}
            >
              {sidebarTop}
            </Div>
          )}

          <AdminNavigationStyled
            aria-label="Main navigation"
            style={{
              position: 'relative',
              top: window.innerWidth > 800 ? 54 : 16,
              transition: '.2s all ease',
              padding: '0 24px',
            }}
          >
            {routes?.map(
              (
                {
                  path,
                  name,
                  Icon,
                  permissions,
                  permission,
                  newItem,
                  Badge: RouteBadge,
                },
                i
              ) => {
                if (
                  name &&
                  permissions.includes(role) &&
                  (!permission || can(permission))
                ) {
                  // A route that brings its own count (the deliveries app's
                  // verifications) wears it; the rest are looked up here.
                  const Badge = RouteBadge ?? badgeFor(path);

                  return (
                    <CustomLink
                      to={path}
                      key={i}
                      onClick={() =>
                        window.innerWidth < 800 && setSidebarIsOpen(false)
                      }
                    >
                      <Div
                        $flex
                        $alignCenter
                        // $justifyCenter={!sidebarIsOpen}
                        $mb="16"
                        $gap="12px"
                        $between
                      >
                        <Div $flex $alignCenter>
                          <NavLinkCircle
                            defaultColor={
                              path === PARTNER_V_DRIVE
                                ? lightTheme.darkBlue
                                : undefined
                            }
                            selected={selected(path)}
                            style={{
                              marginRight:
                                window.innerWidth > 800 && !sidebarIsOpen
                                  ? '0'
                                  : '12px',
                              backgroundColor:
                                path === PARTNER_V_DRIVE
                                  ? lightTheme.white
                                  : undefined,
                            }}
                          >
                            {Icon ? <Icon /> : <File />}
                            {Badge && (
                              <Div
                                $desktop
                                $absolute
                                $top={'-8px'}
                                $right={'-12px'}
                                $opacity={!sidebarIsOpen ? '1' : '0'}
                                style={{ transition: '.2s all ease' }}
                              >
                                <Badge />
                              </Div>
                            )}
                          </NavLinkCircle>
                          <span
                            style={{
                              opacity:
                                window.innerWidth > 800 && !sidebarIsOpen
                                  ? '0'
                                  : '1',
                              flex: 'none',
                              transition: '.2s all ease',
                            }}
                          >
                            {name}
                          </span>
                        </Div>
                        <Div
                          $flex
                          $alignCenter
                          $gap="12px"
                          style={{
                            opacity:
                              window.innerWidth > 800 && !sidebarIsOpen
                                ? '0'
                                : '1',
                            transition: '.2s all ease',
                          }}
                        >
                          {newItem && !hasTwoWeeksPassed(newItem) && (
                            <NewPillIcon />
                          )}
                          {/* After the NEW pill, not before it: the pill is
                              about the menu item and stops appearing after two
                              weeks, while the count is about right now. Read in
                              that order it goes name, what this is, what is
                              waiting - and the number ends up nearest the edge
                              it shares with the collapsed sidebar's badge. */}
                          {Badge && <Badge />}
                          <Div $mobile>
                            <Ic
                              $size="20"
                              $color={lightTheme.white}
                              $rotate="270deg"
                            >
                              <InputArrowIcon color={lightTheme.white} />
                            </Ic>
                          </Div>
                        </Div>
                      </Div>
                    </CustomLink>
                  );
                } else return null;
              }
            )}
          </AdminNavigationStyled>
        </Div>

        <Div
          $width="100%"
          $widthSM="100%"
          $mt={sidebarIsOpen ? '54' : '54'}
          $mtSM="16"
        >
          <Div
            $width="100%"
            $pointer
            $flex
            $alignCenter
            onClick={() => {
              setLogoutModal(true);
            }}
            style={{ transition: '.2s all ease' }}
            $p="0 24px"
            $desktop
          >
            <NavLinkCircle
              style={{
                marginRight:
                  window.innerWidth > 800 && !sidebarIsOpen ? '0' : '12px',
              }}
            >
              <Logout />
            </NavLinkCircle>
            <span
              style={{
                opacity: window.innerWidth > 800 && !sidebarIsOpen ? '0' : '1',
                flex: 'none',
                transition: '.2s all ease',
              }}
            >
              Logout
            </span>
          </Div>
          <Div
            $width="100%"
            $pointer
            $flex
            $alignCenter
            onClick={() => {
              setLogoutModal(true);
              setSidebarIsOpen(false);
            }}
            style={{ transition: '.2s all ease' }}
            $p="0 24px"
            $mobile
          >
            <NavLinkCircle
              style={{
                marginRight:
                  window.innerWidth > 800 && !sidebarIsOpen ? '0' : '12px',
              }}
            >
              <Logout />
            </NavLinkCircle>
            <span
              style={{
                opacity: window.innerWidth > 800 && !sidebarIsOpen ? '0' : '1',
                flex: 'none',
                transition: '.2s all ease',
              }}
            >
              Logout
            </span>
          </Div>
          {isPartner && (
            // <a href="sms:8186007488" style={{ width: '100%' }}>
            //   <Button
            //     $negative
            //     $w="100%"
            //     $wSM="100%"
            //     $mt="20"
            //     $selected
            //     style={{ boxShadow: 'none' }}
            //   >
            //     Contact concierge via text
            //   </Button>
            // </a>
            <Div
              $mt={window.innerWidth > 800 && !sidebarIsOpen ? '0' : '20'}
              $mb={window.innerWidth > 800 && !sidebarIsOpen ? '' : '20'}
              $height={window.innerWidth > 800 && !sidebarIsOpen ? '0' : 'auto'}
              // $heightSM={
              //   window.innerWidth > 800 && !sidebarIsOpen ? '0' : 'auto'
              // }
              style={{ overflow: 'hidden', transition: '.2s all ease' }}
              $opacity={window.innerWidth > 800 && !sidebarIsOpen ? '0' : '1'}
            >
              <Div
                $m="0 16px"
                $minWidth="202px"
                $background={lightTheme.white}
                $borderRadius="16px"
                $p="12px"
                $width="calc(100% - 32px)"
                $between
                $pointer
                onClick={() =>
                  navigate(
                    locations.length === 1
                      ? ADMIN_LOCATIONS_CONNECT_BRANDS.replace(
                          ':id',
                          locations[0].location.id.toString()
                        )
                      : PARTNER_LOCATIONS_CHOOSE_LOCATION_TO_ADD_BRAND
                  )
                }
                $flex
                $alignCenter
                $gap="8px"
                style={{ overflow: 'hidden', transition: '.2s all ease' }}
                $opacity={window.innerWidth > 800 && !sidebarIsOpen ? '0' : '1'}
              >
                <Div $flex $alignCenter $gap="8px">
                  <svg
                    width="30"
                    height="28"
                    viewBox="0 0 30 28"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M22.0109 7.9936C21.9667 7.95861 21.9157 7.93364 21.8612 7.92037C21.8067 7.90709 21.7501 7.90581 21.695 7.91662C21.6134 7.9433 21.5448 8.00043 21.5029 8.0765L21.1768 8.65091L21.8595 9.05359L22.19 8.47178C22.2138 8.4336 22.2284 8.39017 22.2325 8.34511C22.2365 8.30005 22.2299 8.25465 22.2133 8.2127C22.1729 8.11797 22.1014 8.0405 22.0109 7.9936V7.9936Z"
                      fill="#E61E29"
                    />
                    <path
                      d="M19.2772 13.4546C19.2182 13.3956 19.151 13.3457 19.0777 13.3065C19.0095 13.2641 18.9354 13.2321 18.8579 13.2118C18.8026 13.1969 18.7444 13.1969 18.6891 13.2118C18.641 13.226 18.597 13.2519 18.561 13.2873C18.5215 13.3276 18.4873 13.3729 18.4591 13.422L18.1563 13.949L19.0064 14.4494L19.3092 13.9165C19.3376 13.8676 19.3586 13.8147 19.3718 13.7596C19.3841 13.711 19.3841 13.6601 19.3718 13.6115C19.353 13.5523 19.3206 13.4985 19.2772 13.4546Z"
                      fill="#E61E29"
                    />
                    <path
                      d="M23.2179 8.57501C23.1514 8.5336 23.0759 8.50969 22.9981 8.50544C22.9307 8.50253 22.864 8.51949 22.8059 8.55429C22.7453 8.58961 22.6951 8.64071 22.6603 8.70233L22.311 9.31671L23.0563 9.76084L23.4056 9.14646C23.4441 9.08519 23.4662 9.01477 23.4697 8.94216C23.4721 8.87362 23.4527 8.80612 23.4144 8.74971C23.3627 8.6771 23.2955 8.61737 23.2179 8.57501Z"
                      fill="#E61E29"
                    />
                    <path
                      d="M28.6356 2.21679C28.177 1.53631 27.5602 0.981586 26.84 0.601929C26.1198 0.222272 25.3185 0.0294364 24.5074 0.0405583H23.8567C22.0604 0.117541 20.4927 1.32261 19.6891 2.95552L18.6192 4.82235C16.5158 8.47753 15.0252 11.1216 14.491 11.8203C14.0159 12.4539 13.4024 12.9664 12.6991 13.3173C11.9958 13.6683 11.2218 13.8481 10.4384 13.8426C9.83934 13.8338 9.24541 13.7289 8.67851 13.5317C6.53868 12.7545 5.16163 10.8107 5.16163 8.51602C5.16163 6.9216 5.96515 5.59957 6.69153 4.47296C7.41936 3.30639 8.06713 2.29525 7.64644 1.28412C7.42621 0.805107 7.04753 0.419987 6.57653 0.196003C6.30522 0.0600033 6.00552 -0.00705187 5.70313 0.00058663H5.00878C3.32749 0.00058663 1.6069 0.856275 0.727677 2.29525C0.265792 3.03734 0.0141953 3.89466 0.000583215 4.77282C-0.0130289 5.65098 0.211872 6.51593 0.650527 7.27246L10.5912 24.7682C11.0123 25.502 11.615 26.1109 12.3395 26.534C13.064 26.9572 13.8848 27.1798 14.7202 27.1798C15.5557 27.1798 16.3765 26.9572 17.101 26.534C17.8254 26.1109 18.4282 25.502 18.8492 24.7682L28.7885 7.27246C29.2427 6.50368 29.4698 5.61823 29.4427 4.72178C29.4155 3.82533 29.1354 2.95574 28.6356 2.21679V2.21679ZM19.6746 16.9323L19.4315 16.8967C19.4275 17.036 19.4119 17.1747 19.3849 17.3113C19.3513 17.4909 19.2873 17.6632 19.1957 17.8205C19.1104 17.9849 18.9832 18.123 18.8274 18.2202C18.6865 18.299 18.5263 18.3349 18.3659 18.3239C18.1766 18.3085 17.9908 18.2625 17.8157 18.1877C17.5966 18.0968 17.3844 17.9894 17.181 17.8664C16.9685 17.7447 16.7664 17.6051 16.5769 17.4489C16.4241 17.3272 16.296 17.1764 16.1999 17.0048C16.1165 16.8498 16.081 16.6728 16.098 16.497C16.1232 16.2785 16.1963 16.0685 16.312 15.8826C16.3906 15.7445 16.4678 15.6216 16.5435 15.514C16.6191 15.4059 16.689 15.3067 16.7734 15.2179C16.8579 15.1291 16.9336 15.0388 17.018 14.9514L17.4707 15.2179C17.4314 15.2697 17.3805 15.3408 17.3251 15.4296C17.2698 15.5184 17.197 15.6206 17.1243 15.7331C17.0515 15.8456 16.9787 15.9611 16.9103 16.0855C16.8228 16.2163 16.777 16.3714 16.7793 16.5296C16.7987 16.6704 16.87 16.7984 16.9787 16.8878C17.1496 17.0376 17.3355 17.1686 17.5333 17.2787C17.6882 17.373 17.8492 17.4566 18.0151 17.5289C18.1219 17.5768 18.2363 17.6044 18.3528 17.6103C18.4378 17.6145 18.5215 17.5889 18.5901 17.5378C18.6647 17.478 18.7266 17.4034 18.7721 17.3187C18.8306 17.2124 18.8794 17.1009 18.9176 16.9856C18.9509 16.895 18.9791 16.8025 19.0021 16.7087L18.4329 16.3653L18.1228 16.9101L17.6585 16.6362C17.6832 16.5755 17.7153 16.5029 17.7546 16.423C17.7939 16.343 17.8346 16.2572 17.8798 16.1698C17.9249 16.0825 17.9685 15.9996 18.0122 15.9226C18.0559 15.8456 18.1054 15.7583 18.1461 15.6917C18.1869 15.625 18.2262 15.5629 18.264 15.5096L19.9308 16.4926L19.6746 16.9323ZM21.1157 14.4052L19.7546 14.2453L19.6891 14.3548C19.6629 14.3963 19.6396 14.4377 19.6149 14.4807L19.5407 14.6095C19.5159 14.6539 19.4926 14.6909 19.4708 14.7249L20.5654 15.3704L20.2248 15.9626L17.2873 14.2305L17.9962 13.0017C18.0588 12.8894 18.1362 12.7863 18.2262 12.6953C18.308 12.6117 18.4076 12.5484 18.5173 12.5102C18.6446 12.4721 18.7792 12.467 18.9089 12.4954C19.0883 12.535 19.2596 12.6056 19.4155 12.7042C19.5327 12.7711 19.6408 12.8532 19.7372 12.9484C19.8087 13.0188 19.8673 13.1015 19.9104 13.1927C19.9439 13.268 19.9646 13.3486 19.9715 13.431C19.976 13.5071 19.9731 13.5835 19.9628 13.659L21.4592 13.7952L21.1157 14.4052ZM22.551 11.8351C22.4926 12.0045 22.418 12.1677 22.3283 12.3222C22.2419 12.4772 22.1405 12.623 22.0255 12.7574C21.9252 12.8761 21.803 12.9737 21.6659 13.0447C21.5301 13.1105 21.3786 13.1357 21.2292 13.1172C21.0403 13.0909 20.8592 13.0237 20.6979 12.9203L18.7051 11.7463L19.0457 11.1541L21.0356 12.3207C21.1225 12.3773 21.2236 12.4072 21.3268 12.4066C21.4194 12.397 21.5066 12.3576 21.5757 12.2941C21.6635 12.2126 21.7378 12.1171 21.7955 12.0113C21.8564 11.9094 21.9002 11.7978 21.925 11.6812C21.9452 11.5883 21.9355 11.4912 21.8974 11.4043C21.8467 11.3109 21.7696 11.2351 21.6761 11.1867L19.6877 10.0142L20.0312 9.41316L22.024 10.5871C22.1943 10.678 22.3433 10.8053 22.4607 10.9602C22.5485 11.083 22.6014 11.2279 22.6136 11.3792C22.6237 11.5339 22.6024 11.6891 22.551 11.8351V11.8351ZM23.944 9.44129L23.2453 10.6671L20.3078 8.93646L20.9774 7.761C21.1463 7.46491 21.336 7.28825 21.5466 7.23101C21.664 7.20535 21.7854 7.20392 21.9034 7.22681C22.0214 7.24969 22.1337 7.29643 22.2336 7.36424C22.3389 7.42401 22.4331 7.50219 22.5117 7.59519C22.5775 7.67394 22.6243 7.76721 22.6485 7.86759C22.6713 7.96542 22.6678 8.0677 22.6383 8.16368C22.6923 8.10579 22.754 8.05595 22.8217 8.01563C22.8941 7.97438 22.9729 7.94586 23.0546 7.93125C23.147 7.91661 23.2413 7.91963 23.3327 7.94013C23.443 7.96527 23.5487 8.00825 23.6456 8.06745C23.8148 8.15688 23.9586 8.28889 24.0634 8.45088C24.1429 8.58713 24.1723 8.74766 24.1464 8.90389C24.1123 9.09424 24.0437 9.27648 23.944 9.44129V9.44129ZM25.4681 6.66844C25.4138 6.87306 25.3314 7.06891 25.2236 7.25025C25.1799 7.32871 25.1319 7.40718 25.078 7.48712C25.0241 7.56706 24.9717 7.64701 24.915 7.72547C24.8582 7.80393 24.8014 7.87351 24.7461 7.94753C24.6908 8.02155 24.6384 8.07633 24.5874 8.12815L24.1231 7.85427C24.1769 7.77876 24.2352 7.69142 24.2992 7.59223C24.3633 7.49304 24.4273 7.38941 24.4928 7.27986C24.5583 7.17031 24.6195 7.06668 24.6762 6.96749C24.7279 6.87914 24.7698 6.78531 24.8014 6.68769C24.8238 6.61441 24.8238 6.53594 24.8014 6.46266C24.7753 6.39026 24.7233 6.33052 24.6559 6.29537L24.5481 6.23172C24.5127 6.20742 24.4724 6.19138 24.4301 6.18473C24.3878 6.17808 24.3446 6.18098 24.3036 6.19322C24.2175 6.22648 24.1468 6.29149 24.1056 6.37532L23.9601 6.62995C23.8426 6.87303 23.6455 7.06677 23.4025 7.17771C23.1929 7.25765 22.9396 7.20831 22.6427 7.02967L22.4971 6.94528C22.2118 6.77651 22.0502 6.56975 22.0124 6.32498C21.9871 6.03147 22.061 5.73795 22.222 5.49298C22.2846 5.383 22.3526 5.27627 22.4258 5.17321C22.4971 5.07106 22.5714 4.97335 22.6427 4.87712C22.714 4.78089 22.7795 4.70687 22.8363 4.64321L23.3006 4.91709C23.222 5.03553 23.1361 5.16877 23.0415 5.31681C22.9469 5.46485 22.8625 5.60253 22.7882 5.73133C22.7414 5.81333 22.7034 5.90018 22.6747 5.9904C22.6505 6.06188 22.6505 6.13952 22.6747 6.21099C22.7105 6.28741 22.7705 6.34943 22.845 6.38716L22.9251 6.43453C22.9641 6.46272 23.009 6.48116 23.0563 6.48836C23.1036 6.49556 23.1519 6.49132 23.1973 6.47599C23.2916 6.42825 23.3679 6.3502 23.4142 6.25392L23.583 5.95784C23.6653 5.79965 23.7861 5.66559 23.9339 5.56848C24.0597 5.49184 24.2052 5.45524 24.3516 5.46337C24.5065 5.47773 24.656 5.5284 24.7883 5.61142L24.9557 5.7106C25.1482 5.80746 25.3063 5.96263 25.4084 6.15473C25.4877 6.31333 25.5088 6.49543 25.4681 6.66844V6.66844Z"
                      fill="#E61E29"
                    />
                    <path
                      d="M10.399 4.62246C9.63509 4.62246 8.88838 4.85284 8.25327 5.28448C7.61816 5.71611 7.12317 6.3296 6.83092 7.04736C6.53867 7.76512 6.46227 8.5549 6.6114 9.31681C6.76053 10.0787 7.12848 10.7786 7.66872 11.3278C8.20896 11.877 8.89722 12.2509 9.64644 12.4023C10.3957 12.5537 11.1722 12.4757 11.8778 12.1782C12.5835 11.8807 13.1865 11.3771 13.6107 10.731C14.0349 10.0849 14.2611 9.32542 14.2608 8.54855C14.2506 7.51026 13.8404 6.51746 13.1183 5.78338C12.3962 5.04929 11.4199 4.63248 10.399 4.62246V4.62246ZM10.9623 10.4953H9.88803L8.76134 6.74687H9.65075L10.4295 9.55969L11.201 6.74687H12.0744L10.9623 10.4953Z"
                      fill="#E61E29"
                    />
                  </svg>
                  <Div>
                    <Disclaimer $bold $m="0" $color={lightTheme.primary}>
                      Need More Revenue?
                    </Disclaimer>
                    <Disclaimer
                      style={{ fontSize: '10px' }}
                      $m="0"
                      $color={lightTheme.primary}
                    >
                      Add New Virtual Brands.
                    </Disclaimer>
                  </Div>
                </Div>
                <Ic $size={16}>
                  <ArrowRed />
                </Ic>
              </Div>
            </Div>
          )}
        </Div>
      </AdminSidebarStyled>

      <MobileSidebarOverlay
        $sidebarIsOpen={sidebarIsOpen}
        onClick={() => setSidebarIsOpen(false)}
      />

      {/* LOGOUT MODAL */}
      <AdminLogoutModal isOpen={logoutModal} setIsOpen={setLogoutModal} />
    </>
  );
};

const CustomLink = ({
  to,
  ...props
}: {
  to: string;
} & React.ComponentProps<typeof Link>) => {
  const location = useLocation();

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (location.pathname === to) {
      event.preventDefault();
    }
  };

  if (location.pathname === to) {
    return <Div $pointer {...(props as any)}></Div>;
  }

  return <Link to={to} onClick={handleClick} {...props} />;
};

const LogoWhiteCircle = () => {
  return (
    <svg
      width="60"
      height="60"
      viewBox="0 0 115 115"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <ellipse cx="57.5" cy="57.3877" rx="57.3877" ry="57.3877" fill="white" />
      <path
        d="M75.6446 48.1391C75.5338 48.0529 75.4059 47.9914 75.2694 47.9587C75.133 47.926 74.9911 47.9229 74.8533 47.9495C74.6488 48.0152 74.4769 48.1559 74.372 48.3433L73.5552 49.7581L75.2653 50.7499L76.0931 49.3169C76.1529 49.2229 76.1894 49.1159 76.1995 49.0049C76.2097 48.8939 76.1932 48.7821 76.1514 48.6788C76.0503 48.4455 75.8711 48.2546 75.6446 48.1391V48.1391Z"
        fill="#E61E29"
      />
      <path
        d="M68.7965 61.5908C68.6487 61.4454 68.4804 61.3226 68.2969 61.2262C68.126 61.1215 67.9404 61.0429 67.7463 60.9928C67.6077 60.9562 67.462 60.9562 67.3233 60.9928C67.2028 61.0277 67.0927 61.0915 67.0025 61.1788C66.9037 61.2781 66.8179 61.3896 66.7472 61.5106L65.9888 62.8087L68.1183 64.0412L68.8767 62.7285C68.9478 62.6081 69.0006 62.4779 69.0335 62.342C69.0644 62.2224 69.0644 62.0969 69.0335 61.9773C68.9865 61.8314 68.9053 61.6989 68.7965 61.5908Z"
        fill="#E61E29"
      />
      <path
        d="M78.6677 49.5723C78.5013 49.4703 78.312 49.4114 78.1171 49.4009C77.9484 49.3938 77.7812 49.4356 77.6358 49.5213C77.484 49.6083 77.3581 49.7341 77.2711 49.8859L76.396 51.3992L78.263 52.4931L79.1381 50.9798C79.2345 50.8289 79.2898 50.6555 79.2985 50.4766C79.3046 50.3078 79.256 50.1415 79.16 50.0026C79.0306 49.8238 78.8622 49.6766 78.6677 49.5723Z"
        fill="#E61E29"
      />
      <path
        d="M92.2394 33.9108C91.0905 32.2347 89.5454 30.8684 87.7413 29.9333C85.9372 28.9982 83.9301 28.5232 81.8982 28.5506H80.2683C75.7686 28.7402 71.8415 31.7084 69.8286 35.7303L67.1485 40.3285C61.8795 49.3314 58.1456 55.8439 56.8074 57.565C55.6172 59.1254 54.0806 60.3878 52.3187 61.2522C50.5569 62.1167 48.6182 62.5596 46.6558 62.546C45.1551 62.5242 43.6673 62.2658 42.2473 61.7802C36.8871 59.8659 33.4376 55.0782 33.4376 49.4262C33.4376 45.4991 35.4504 42.2428 37.2699 39.4679C39.0931 36.5945 40.7158 34.1041 39.662 31.6136C39.1103 30.4337 38.1617 29.4851 36.9819 28.9335C36.3022 28.5985 35.5515 28.4333 34.794 28.4521H33.0547C28.8431 28.4521 24.5331 30.5597 22.3306 34.1041C21.1736 35.9319 20.5434 38.0435 20.5093 40.2065C20.4752 42.3694 21.0385 44.4999 22.1374 46.3633L47.0386 89.4564C48.0933 91.264 49.6032 92.7636 51.418 93.8059C53.2327 94.8481 55.2889 95.3965 57.3817 95.3965C59.4744 95.3965 61.5306 94.8481 63.3454 93.8059C65.1601 92.7636 66.67 91.264 67.7247 89.4564L92.6223 46.3633C93.76 44.4697 94.3289 42.2888 94.261 40.0808C94.1931 37.8727 93.4913 35.7309 92.2394 33.9108V33.9108ZM69.7922 70.156L69.1832 70.0685C69.1732 70.4115 69.1342 70.7531 69.0665 71.0895C68.9824 71.5319 68.822 71.9564 68.5925 72.3439C68.3789 72.7488 68.0602 73.0889 67.67 73.3284C67.317 73.5224 66.9159 73.6109 66.5141 73.5836C66.0397 73.5457 65.5745 73.4325 65.1357 73.2482C64.5868 73.0244 64.0554 72.7599 63.5459 72.4569C63.0135 72.1572 62.5073 71.8132 62.0326 71.4286C61.6497 71.1288 61.3289 70.7573 61.0882 70.3347C60.8792 69.953 60.7903 69.5171 60.833 69.084C60.8961 68.5458 61.0792 68.0286 61.369 67.5707C61.5659 67.2304 61.7592 66.9277 61.9488 66.6628C62.1384 66.3966 62.3134 66.1523 62.5249 65.9335C62.7364 65.7147 62.926 65.4923 63.1375 65.2771L64.2715 65.9335C64.1731 66.0611 64.0455 66.2361 63.9069 66.4549C63.7683 66.6737 63.586 66.9253 63.4037 67.2024C63.2214 67.4796 63.039 67.764 62.8677 68.0703C62.6484 68.3925 62.5339 68.7745 62.5395 69.1642C62.5882 69.511 62.7667 69.8264 63.039 70.0466C63.4671 70.4154 63.9327 70.738 64.4283 71.0093C64.8163 71.2416 65.2196 71.4475 65.6353 71.6255C65.9027 71.7435 66.1894 71.8115 66.4813 71.8261C66.694 71.8364 66.9038 71.7733 67.0756 71.6474C67.2624 71.5001 67.4175 71.3166 67.5314 71.1077C67.6781 70.8461 67.8002 70.5715 67.8961 70.2873C67.9794 70.0642 68.05 69.8365 68.1075 69.6054L66.6818 68.7595L65.9051 70.1013L64.7419 69.4267C64.8039 69.2772 64.8841 69.0986 64.9826 68.9017C65.081 68.7048 65.1831 68.4933 65.2962 68.2781C65.4092 68.063 65.5186 67.8588 65.628 67.6692C65.7374 67.4796 65.8614 67.2644 65.9635 67.1003C66.0656 66.9363 66.164 66.7831 66.2588 66.6518L70.4339 69.073L69.7922 70.156ZM73.4021 63.9316L69.9927 63.5378L69.8286 63.8076C69.763 63.9097 69.7047 64.0118 69.6427 64.1176L69.4567 64.4348C69.3947 64.5442 69.3364 64.6354 69.2817 64.7192L72.0238 66.3091L71.1705 67.7676L63.8121 63.5013L65.5879 60.4748C65.7447 60.1982 65.9385 59.9442 66.164 59.72C66.3689 59.5141 66.6184 59.3582 66.8933 59.2642C67.2121 59.1704 67.5493 59.1579 67.8742 59.2278C68.3235 59.3251 68.7527 59.4991 69.1431 59.7419C69.4367 59.9067 69.7075 60.109 69.949 60.3436C70.1281 60.5168 70.275 60.7205 70.3829 60.9452C70.4668 61.1308 70.5186 61.3293 70.5361 61.5323C70.5472 61.7197 70.5399 61.9078 70.5142 62.0938L74.2627 62.4293L73.4021 63.9316ZM76.9975 57.6015C76.8513 58.0187 76.6644 58.4205 76.4396 58.8011C76.2232 59.1829 75.9691 59.5421 75.6811 59.8732C75.4299 60.1654 75.1239 60.4057 74.7805 60.5806C74.4402 60.7429 74.0608 60.8049 73.6865 60.7593C73.2134 60.6944 72.7596 60.529 72.3556 60.2743L67.3637 57.3827L68.2169 55.9241L73.2016 58.7975C73.4191 58.9369 73.6724 59.0104 73.9309 59.009C74.1629 58.9854 74.3814 58.8883 74.5544 58.7319C74.7745 58.5311 74.9605 58.2959 75.105 58.0354C75.2576 57.7843 75.3673 57.5094 75.4295 57.2222C75.4801 56.9934 75.4558 56.7544 75.3602 56.5404C75.2333 56.3102 75.0402 56.1235 74.806 56.0043L69.825 53.1164L70.6855 51.636L75.6775 54.5275C76.1041 54.7514 76.4773 55.0648 76.7714 55.4464C76.9912 55.7488 77.1237 56.1058 77.1543 56.4784C77.1796 56.8595 77.1262 57.2418 76.9975 57.6015V57.6015ZM80.4871 51.7052L78.7368 54.7245L71.3784 50.4618L73.0557 47.5666C73.4787 46.8373 73.9539 46.4022 74.4815 46.2612C74.7757 46.198 75.0796 46.1944 75.3753 46.2508C75.6709 46.3072 75.9522 46.4223 76.2026 46.5893C76.4663 46.7366 76.7021 46.9291 76.899 47.1582C77.0639 47.3521 77.1812 47.5819 77.2418 47.8291C77.2989 48.0701 77.2901 48.322 77.2163 48.5584C77.3515 48.4158 77.5061 48.293 77.6757 48.1937C77.8571 48.0922 78.0543 48.0219 78.2591 47.9859C78.4906 47.9499 78.7268 47.9573 78.9556 48.0078C79.232 48.0697 79.4967 48.1756 79.7396 48.3214C80.1633 48.5417 80.5236 48.8668 80.7861 49.2658C80.9853 49.6014 81.0589 49.9968 80.9939 50.3816C80.9086 50.8504 80.7367 51.2993 80.4871 51.7052V51.7052ZM84.3049 44.8755C84.1687 45.3795 83.9625 45.8619 83.6923 46.3086C83.5829 46.5018 83.4625 46.6951 83.3276 46.892C83.1927 47.0889 83.0614 47.2858 82.9192 47.4791C82.777 47.6723 82.6348 47.8437 82.4962 48.026C82.3577 48.2083 82.2264 48.3433 82.0988 48.4709L80.9356 47.7963C81.0705 47.6103 81.2164 47.3952 81.3768 47.1509C81.5372 46.9066 81.6977 46.6513 81.8618 46.3815C82.0259 46.1117 82.179 45.8564 82.3212 45.6121C82.4505 45.3945 82.5557 45.1634 82.6348 44.9229C82.6909 44.7424 82.6909 44.5492 82.6348 44.3687C82.5694 44.1903 82.4392 44.0432 82.2702 43.9566L82.0003 43.7998C81.9115 43.74 81.8105 43.7005 81.7046 43.6841C81.5987 43.6677 81.4905 43.6749 81.3877 43.705C81.172 43.787 80.9949 43.9471 80.8918 44.1535L80.5272 44.7807C80.2331 45.3794 79.7391 45.8566 79.1306 46.1299C78.6055 46.3268 77.9711 46.2052 77.2272 45.7652L76.8626 45.5574C76.1479 45.1417 75.7431 44.6324 75.6483 44.0296C75.5849 43.3066 75.7701 42.5837 76.1734 41.9803C76.3303 41.7094 76.5007 41.4465 76.6839 41.1927C76.8626 40.9411 77.0485 40.7004 77.2272 40.4634C77.4059 40.2264 77.57 40.044 77.7122 39.8872L78.8754 40.5618C78.6785 40.8535 78.4633 41.1817 78.2263 41.5464C77.9893 41.911 77.7778 42.2501 77.5918 42.5674C77.4746 42.7693 77.3792 42.9833 77.3074 43.2055C77.2468 43.3815 77.2468 43.5727 77.3074 43.7488C77.3972 43.937 77.5474 44.0898 77.734 44.1827L77.9346 44.2994C78.0322 44.3688 78.1449 44.4142 78.2633 44.432C78.3818 44.4497 78.5028 44.4393 78.6165 44.4015C78.8528 44.2839 79.0438 44.0917 79.1598 43.8545L79.5828 43.1252C79.7888 42.7356 80.0914 42.4054 80.4616 42.1662C80.7768 41.9775 81.1412 41.8873 81.5081 41.9074C81.896 41.9427 82.2704 42.0675 82.602 42.272L83.0213 42.5163C83.5034 42.7549 83.8996 43.1371 84.1554 43.6102C84.3539 44.0009 84.4069 44.4494 84.3049 44.8755V44.8755Z"
        fill="#E61E29"
      />
      <path
        d="M46.5572 39.8362C44.6437 39.8362 42.7732 40.4036 41.1823 41.4668C39.5914 42.5299 38.3514 44.041 37.6193 45.8089C36.8873 47.5768 36.6959 49.5221 37.0695 51.3987C37.443 53.2754 38.3647 54.9991 39.718 56.3519C41.0713 57.7046 42.7954 58.6257 44.6722 58.9986C46.549 59.3714 48.4942 59.1793 50.2618 58.4466C52.0294 57.7138 53.54 56.4733 54.6025 54.882C55.6651 53.2906 56.2318 51.4199 56.2311 49.5064C56.2055 46.949 55.1779 44.5037 53.3691 42.6956C51.5603 40.8875 49.1146 39.8609 46.5572 39.8362V39.8362ZM47.9684 54.3015H45.2773L42.455 45.0688H44.683L46.6338 51.9969L48.5664 45.0688H50.7542L47.9684 54.3015Z"
        fill="#E61E29"
      />
    </svg>
  );
};
